import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { hideOverlappingXaxisAnnotationLabels, moveAnnotationsToBack } from "../lib/apexcharts_annotations"
import { buildCsvFromConfig, formatDateToYMD, resolveCsvExportOptions } from "../lib/apex_chart_csv"
import { assignSeriesToYAxes } from "../lib/apex_chart_yaxis"
import {
  applyPassthroughOptions,
  applyViewportOptions,
  buildDefaultLegend,
  buildDefaultTooltip,
  configuredTooltipOptions,
  estimateDataLabelWidth,
  estimatePlotWidth,
  limitDataLabelFlags,
  resolveDatetimeTickAmount,
  resolveInternalOption,
  resolveVisibleIndexRange,
  usesCustomDataLabelSampling,
  usesCustomResponsive
} from "../lib/apex_chart_options"
import { buildSeriesValueFormatter, formatCompactNumber, resolveYAxisLabelFormatter } from "../lib/apex_chart_label_formatters"
import { isChartWidthOutOfSync, isMobileViewport, waitForStableSize, watchMobileViewport } from "../lib/apex_chart_viewport"
import { buildDiffSummary } from "../lib/apex_chart_diff"
import { ChartClickGuard } from "../lib/apex_chart_click_guard"

const CLICKABLE_CURSOR_STYLE_ID = 'apex-chart-clickable-style'

// 幅が 768px をまたいだとき、作り直すまで待つ時間(ms)。ウィンドウをドラッグで広げ縮めしたときに何度も作り直さないため。
// この後さらにコンテナの幅が止まるのを待つ(waitForStableSize)
const VIEWPORT_REBUILD_DELAY_MS = 100

function ensureClickableCursorStyles() {
  if (typeof document === 'undefined') {
    return
  }

  if (document.getElementById(CLICKABLE_CURSOR_STYLE_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = CLICKABLE_CURSOR_STYLE_ID
  style.textContent = `
    .apex-chart--clickable,
    .apex-chart--clickable .apexcharts-canvas,
    .apex-chart--clickable .apexcharts-series,
    .apex-chart--clickable .apexcharts-series path,
    .apex-chart--clickable .apexcharts-series circle,
    .apex-chart--clickable .apexcharts-marker,
    .apex-chart--clickable .apexcharts-datalabel,
    .apex-chart--clickable .apexcharts-bar-area,
    .apex-chart--clickable .apexcharts-region,
    .apex-chart--clickable .apexcharts-xcrosshairs,
    .apex-chart--clickable .apexcharts-ycrosshairs,
    .apex-chart--clickable .apexcharts-tooltip {
      cursor: pointer !important;
    }
  `
  document.head.appendChild(style)
}

const DEFAULT_CSV_DATE_FORMATTER = (row, config) => {
  // row[0] は X 値（ミリ秒タイムスタンプや日付文字列）
  const rawValue = Array.isArray(row) ? row[0] : row
  const formatted = formatDateToYMD(rawValue)
  if (!formatted) {
    return row
  }

  // ApexCharts の CSV では X 列を文字列に差し替えた配列を返す必要がある
  const value = formatted
  if (Array.isArray(row)) {
    return [value, ...row.slice(1)]
  }
  return value
}

const DEFAULT_CSV_CATEGORY_FORMATTER = (value, timestamp) => {
  const formatted = formatDateToYMD(timestamp ?? value)
  if (formatted) {
    return formatted
  }

  if (typeof value === 'string') {
    const fallback = formatDateToYMD(value)
    if (fallback) {
      return fallback
    }
  }

  return value
}

// URLパスからSNSプラットフォーム名を判定する
const PLATFORM_BY_PATH = [
  [/^\/(cl_inst|inst_|instagram)/, 'Instagram'],
  [/^\/(cl_tw|cl_x_manage|official_twitter|tw_keyword|x_scheduled)/, 'X'],
  [/^\/(cl_tiktok|tiktok)/, 'TikTok'],
  [/^\/(cl_fb|fb_)/, 'Facebook'],
  [/^\/cl_line/, 'LINE'],
  [/^\/(cl_yt|yt_)/, 'YouTube'],
  [/^\/(cl_thread|thread)/, 'Threads'],
  [/^\/cl_bluesky/, 'Bluesky']
]

const CSV_FILENAME_PART_MAX_LENGTH = 80

const sanitizeCsvFilenamePart = (value) => {
  const sanitized = String(value)
    .trim()
    .replace(/\s*\/\s*/g, '・')
    .replace(/\s+/g, '')
    .replace(/[\\/:*?"<>|,]/g, '')
    .replace(/[. ]+$/g, '')

  const characters = Array.from(sanitized)
  if (characters.length <= CSV_FILENAME_PART_MAX_LENGTH) return sanitized

  return `${characters.slice(0, CSV_FILENAME_PART_MAX_LENGTH - 1).join('')}…`
}

// CSV専用情報は画面タイトルとは分離し、チャートまたは祖先要素のdata属性から取得する。
// config.export.metadata は動的生成するチャートなど、data属性を付けにくい場合に利用できる。
const resolveCsvExportMetadata = (config, element) => {
  const scope = element.closest('[data-chart-export-entity]')
  const configured = config.export?.metadata ||
                     config.options?.export?.metadata ||
                     config.options?.chart_options?.export?.metadata ||
                     {}

  return {
    entity: configured.entity || element.dataset.chartExportEntity || scope?.dataset.chartExportEntity,
    identifier: configured.identifier || element.dataset.chartExportIdentifier || scope?.dataset.chartExportIdentifier,
    platform: configured.platform || element.dataset.chartExportPlatform || scope?.dataset.chartExportPlatform,
    chart: configured.chart || element.dataset.chartExportTitle
  }
}

// アカウント名/キーワード名: 画面ヘッダーの page_title（例: "@ywp5re9p - Instagram管理アカウント詳細"）を優先し、
// なければ <title> の先頭セグメントを使う。「〜の詳細分析」等の定型サフィックスは除去する
const resolvePageEntity = () => {
  const headerText = document.querySelector('#layout-content > div > h3')?.textContent?.trim()
  const titleHead = (document.title || '').split(' - ')[0]?.trim()
  const candidate = (headerText ? headerText.split(' - ')[0].trim() : '') ||
                    (titleHead && titleHead !== 'Tofu Analytics' ? titleHead : '')
  if (!candidate) return null
  return candidate.replace(/(の詳細分析|のダッシュボード|の詳細|の分析)$/, '')
}

// チャート名: カード内でチャートより前にある最後の可視見出し（内訳チャートのh4などにも対応）。
// ドロップダウン内などの非表示見出し（例: 日付プリセットの「プリセット期間」）は除外する
const resolveChartHeading = (element) => {
  const scope = element.closest('.card') || element.closest('.card-body')
  if (!scope) return null
  const headings = Array.from(scope.querySelectorAll('.card-title, h2, h3, h4'))
    .filter((h) => h.offsetParent !== null)
  const preceding = headings.filter((h) => h.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)
  return preceding.length ? preceding[preceding.length - 1].textContent : null
}

// CSVのファイル名を「{対象名}_{識別子}_{SNSプラットフォーム}_{チャート名}_{開始日-終了日}」形式で組み立てる。
// 取得できなかった要素は省き、名前が何も取れない場合はチャートIDに落とす。
const buildCsvFilename = (config, element) => {
  const parts = []
  const metadata = resolveCsvExportMetadata(config, element)

  const entity = metadata.entity || resolvePageEntity()
  if (entity && sanitizeCsvFilenamePart(entity)) {
    parts.push(sanitizeCsvFilenamePart(entity))
  }

  if (metadata.identifier && sanitizeCsvFilenamePart(metadata.identifier)) {
    parts.push(sanitizeCsvFilenamePart(metadata.identifier))
  }

  const platform = metadata.platform ||
                   PLATFORM_BY_PATH.find(([pattern]) => pattern.test(location.pathname))?.[1]
  if (platform) {
    parts.push(sanitizeCsvFilenamePart(platform))
  }

  const heading = metadata.chart || resolveChartHeading(element)
  if (heading && sanitizeCsvFilenamePart(heading)) {
    parts.push(sanitizeCsvFilenamePart(heading))
  }

  if (parts.length === 0) {
    parts.push(sanitizeCsvFilenamePart(config.chart?.id || 'chart'))
  }

  // 期間: datetime軸はデータのx値、category軸は日付形式のカテゴリから取得
  const toYMD = (value) => formatDateToYMD(value)?.replace(/-/g, '') || null
  let from = null
  let to = null
  const xaxisType = config.xaxis?.type || config.options?.xaxis_type
  if (xaxisType === 'datetime') {
    const xs = []
    config.series?.forEach((s) => {
      if (!Array.isArray(s.data)) return
      s.data.forEach((point) => {
        if (point !== null && typeof point === 'object' && point.x !== undefined) xs.push(point.x)
      })
    })
    if (xs.length) {
      from = toYMD(Math.min(...xs))
      to = toYMD(Math.max(...xs))
    }
  } else {
    const categories = config.xaxis?.categories
    // 「2026-06-01」のような日付文字列カテゴリのみ期間として扱う（数値カテゴリの誤日付化を防ぐ）
    const isDateLike = (value) => typeof value === 'string' && /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(value)
    if (categories?.length && isDateLike(categories[0]) && isDateLike(categories[categories.length - 1])) {
      from = toYMD(categories[0])
      to = toYMD(categories[categories.length - 1])
    }
  }
  if (from && to) {
    parts.push(`${from}-${to}`)
  }

  return parts.join('_')
}

// Apex の exportToCSV 用グローバル設定に日付フォーマッタを注入
if (typeof window !== 'undefined') {
  window.Apex = window.Apex || {}

  // ApexCharts 5.13以降は tooltip.arrow がデフォルトtrue。
  // v4までの外観を維持するため全チャート共通で矢印を無効化する
  window.Apex.tooltip = { arrow: false, ...window.Apex.tooltip }

  window.Apex.chart = window.Apex.chart || {}
  window.Apex.chart.toolbar = window.Apex.chart.toolbar || {}
  window.Apex.chart.toolbar.export = window.Apex.chart.toolbar.export || {}
  window.Apex.chart.toolbar.export.csv = window.Apex.chart.toolbar.export.csv || {}

  const existingFormatter = window.Apex.chart.toolbar.export.csv.dateFormatter
  if (typeof existingFormatter !== 'function') {
    window.Apex.chart.toolbar.export.csv.dateFormatter = DEFAULT_CSV_DATE_FORMATTER
  }

  const existingCategoryFormatter = window.Apex.chart.toolbar.export.csv.categoryFormatter
  if (typeof existingCategoryFormatter !== 'function') {
    window.Apex.chart.toolbar.export.csv.categoryFormatter = DEFAULT_CSV_CATEGORY_FORMATTER
  }
}

export default class extends Controller {
  static targets = ["container"]
  static values = { config: Object }

  connect() {
    ensureClickableCursorStyles()
    this.setupCsvExportOverride()
    this.setupClickGuard()
    this.setupViewportWatcher()
    this.renderChart()
  }

  disconnect() {
    this.teardownViewportWatcher()
    this.cancelWidthSyncUpdate()
    this.teardownClickGuard()

    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    if (this.handleCsvExportClick) {
      this.element.removeEventListener('click', this.handleCsvExportClick, true)
      this.handleCsvExportClick = null
    }

    this.element.classList.remove('apex-chart--clickable')
    this.cleanupLegendTooltip()
  }

  // ドラッグでズームしたときに、点・柱の詳細ページへ遷移しないよう、押した位置を capture 相で記録する(lib/apex_chart_click_guard.js)。
  // Turbo のキャッシュ復帰などで connect が続けて呼ばれても二重に登録しないよう、先に外してから登録する
  setupClickGuard() {
    this.teardownClickGuard()
    this.clickGuard = new ChartClickGuard()
    this.handleClickGuardPointerStart = (event) => this.clickGuard?.handlePointerStart(event)
    this.element.addEventListener('mousedown', this.handleClickGuardPointerStart, true)
    this.element.addEventListener('touchstart', this.handleClickGuardPointerStart, { capture: true, passive: true })
  }

  teardownClickGuard() {
    if (this.handleClickGuardPointerStart) {
      this.element.removeEventListener('mousedown', this.handleClickGuardPointerStart, true)
      this.element.removeEventListener('touchstart', this.handleClickGuardPointerStart, true)
      this.handleClickGuardPointerStart = null
    }
    if (this.clickGuard) {
      this.clickGuard.reset()
      this.clickGuard = null
    }
  }

  // スマホ幅(768px 未満)⇔ それ以上 をまたいだら、その幅の設定でチャートを作り直す(#1486)。
  // ApexCharts の responsive は幅を広げても設定が元に戻らないため使わない(lib/apex_chart_viewport.js)。
  // Turbo のキャッシュ復帰などで connect が続けて呼ばれても二重に登録しないよう、先に外してから登録する
  setupViewportWatcher() {
    this.teardownViewportWatcher()
    this.isMobileLayout = isMobileViewport()
    this.stopWatchingViewport = watchMobileViewport(undefined, (isMobile) => this.handleViewportChange(isMobile))
  }

  teardownViewportWatcher() {
    if (this.stopWatchingViewport) {
      this.stopWatchingViewport()
      this.stopWatchingViewport = null
    }
    clearTimeout(this.viewportRebuildTimer)
    this.viewportRebuildTimer = null
    this.cancelWaitForStableWidth()
  }

  cancelWaitForStableWidth() {
    if (this.stopWaitingForStableWidth) {
      this.stopWaitingForStableWidth()
      this.stopWaitingForStableWidth = null
    }
  }

  // コンテナの幅が止まってから(サイドバーの出し入れの transition が終わってから)作り直す。
  // 動いている途中の幅で作り直すと、ApexCharts は入場アニメーションが終わるまで親要素の幅の変化を反映しないため、
  // 1〜3 秒ほどカードからはみ出していた(#1486 の ui-reviewer 指摘)
  scheduleRebuildAfterLayout() {
    this.cancelWaitForStableWidth()
    this.stopWaitingForStableWidth = waitForStableSize({
      measure: () => this.measureChartWidth(),
      onStable: () => {
        this.stopWaitingForStableWidth = null
        this.rebuildChart()
      }
    })
  }

  handleViewportChange(isMobile) {
    this.isMobileLayout = isMobile
    // 呼び出し側が responsive を渡したチャートは ApexCharts に任せる
    if (!this.usesBuiltinViewportOptions) return

    // 幅が止まるのを待っている途中でまたいだら、待ち直す
    this.cancelWaitForStableWidth()
    clearTimeout(this.viewportRebuildTimer)
    this.viewportRebuildTimer = setTimeout(() => {
      this.viewportRebuildTimer = null
      // 広げ縮めして元の側に戻っていれば作り直さない
      if (this.isMobileLayout === this.renderedMobileLayout) return
      this.viewportRebuildRetried = false
      this.scheduleRebuildAfterLayout()
    }, VIEWPORT_REBUILD_DELAY_MS)
  }

  // いまの幅の設定でチャートを作り直す。既定値は毎回 configValue から組み立て直すので、
  // スマホ幅で足した設定(ツールバー非表示・小さいフォント・データラベルの間引き・右軸の非表示)は残らない。
  // 凡例で隠した系列やズームは初期状態に戻る
  rebuildChart() {
    if (!this.element.isConnected) return

    this.isViewportRebuild = true
    this.cancelWidthSyncUpdate()
    // 作り直す前のチャートで保留した遷移は捨てる
    this.clickGuard?.reset()
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    this.renderChart()
  }

  // ツールバーの「Download CSV」クリックを capture 段階で横取りし、
  // 元データ（configValue）から生成した CSV をダウンロードさせる。
  // 標準の exportToCSV は非表示系列の列が空になるため使わない。
  setupCsvExportOverride() {
    this.handleCsvExportClick = (event) => {
      const menuItem = event.target.closest?.('.apexcharts-menu-item.exportCSV')
      if (!menuItem) return

      const csv = buildCsvFromConfig(this.configValue)
      if (csv === null) return // 円グラフ等、系列がdata配列を持たない形式は標準処理に任せる

      event.preventDefault()
      event.stopImmediatePropagation()
      this.closeToolbarMenu()
      this.triggerCsvDownload(csv)
    }
    this.element.addEventListener('click', this.handleCsvExportClick, true)
  }

  closeToolbarMenu() {
    this.element.querySelectorAll('.apexcharts-menu.apexcharts-menu-open').forEach((menu) => {
      menu.classList.remove('apexcharts-menu-open')
    })
  }

  triggerCsvDownload(csv) {
    const config = this.configValue
    const csvOptions = resolveCsvExportOptions(config)
    const baseFilename = String(csvOptions.filename || buildCsvFilename(config, this.element))
      .replace(/\.csv$/i, '')
    const filename = `${baseFilename}.csv`

    const link = document.createElement('a')
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  renderChart() {
    const config = this.configValue
    
    // すべてのseriesがhiddenの場合の対策
    this.ensureVisibleSeries(config)
    
    // 差分データがある場合の処理
    if (config.diff?.enabled) {
      this.setupDiffTooltips(config)
    }
    
    if (config.options?.keyword_breakdown) {
      this.setupKeywordBreakdownTooltip(config)
    }

    const mobile = Boolean(this.isMobileLayout)
    const apexOptions = this.buildApexOptions(config, { mobile })
    this.renderedMobileLayout = mobile
    this.updateClickableClass(config)
    const chart = new ApexCharts(this.containerTarget, apexOptions)
    this.chart = chart
    const isViewportRebuild = Boolean(this.isViewportRebuild)
    this.isViewportRebuild = false
    chart.render().then(() => {
      // 描画中に作り直された(幅が 768px をまたいだ)ときは古いチャートの後処理をしない
      if (this.chart !== chart) return
      // 幅をまたいで作り直したのに、描いた幅がコンテナとずれていれば(幅が止まったと判定した後にまた動いた)、
      // 幅が止まるのを待ってもう1回だけ作り直す
      if (isViewportRebuild && !this.viewportRebuildRetried &&
          isChartWidthOutOfSync({ containerWidth: this.measureChartWidth(), chartWidth: chart.w?.globals?.svgWidth })) {
        this.viewportRebuildRetried = true
        this.scheduleRebuildAfterLayout()
        return
      }
      this.updateClickableClass(config)
      moveAnnotationsToBack(this.chart?.el)
      this.hideOverlappingAnnotationLabels(config)
      this.setupLegendTooltips(config)
    }).catch((error) => {
      console.error('ApexCharts render error', error)
    })
  }

  // mobile: スマホ幅(768px 未満)の設定を重ねるか(renderChart が描画時の幅で決める)
  buildApexOptions(config, { mobile = false } = {}) {
    // tooltip の目印は、ヘルパー経由(config.options.tooltip)と手組み(config.options.chart_options.tooltip)のどちらに書かれていても読む。
    // ストーリー用の設定は chart_options.tooltip を置き換えるので、その前に読んでおく
    const configuredTooltips = configuredTooltipOptions(config.options)
    const tooltipShowsTime = configuredTooltips.some((tooltip) => tooltip.x?.format === "datetime_with_time")

    // カスタムtooltipの処理
    if (configuredTooltips.some((tooltip) => tooltip.custom === "story_datetime")) {
      this.setupStoryDatetimeTooltip(config)
    }

    const yaxisLabelFormatter = this.resolveChartYAxisLabelFormatter(config.options?.yaxis_label_formatter)
    // 軸ごとの labels.formatter(名前)があればそれを、無ければチャート全体の formatter を使う
    const resolveAxisLabelFormatter = (axis) => resolveYAxisLabelFormatter(axis?.labels?.formatter, yaxisLabelFormatter)
    // ツールチップとデータラベルの値も、系列が載る軸と同じ書式で出す
    this.formatSeriesValue = buildSeriesValueFormatter({
      yaxis: config.yaxis,
      chartFormatterName: config.options?.yaxis_label_formatter
    })
    // 目盛り数を自動で決める datetime 軸の点数(対象外なら null)。
    // ヒートマップ(x 軸は時間帯などのカテゴリ)は幅を見ず、従来どおり1日1目盛り
    const autoTickPointCount = config.xaxis?.tickAmount === undefined && config.series[0]?.data?.length >= 10 && config.options?.xaxis_type === 'datetime'
      ? config.series[0].data.length
      : null
    const isHeatmap = config.chart.type === 'heatmap'
    // デスクトップ幅のデータラベルの間引き(#1471)。buildDataLabelsOptions が使うチャートだけ true にする(ヒートマップは使わない)
    this.dataLabelWidthLimitEnabled = false
    const baseOptions = {
      chart: {
        type: config.chart.type,
        height: config.chart.height,
        id: config.chart.id,
        ...(config.chart.stacked !== undefined ? { stacked: config.chart.stacked } : {}),
        fontFamily: 'inherit',
        toolbar: this.buildToolbarOptions(config)
      },
      series: config.series,
      xaxis: {
        // 日本時間設定 - xaxis_typeがあれば優先
        ...(config.options?.xaxis_type === 'datetime' ? {
          type: 'datetime'
        } : {}),
        ...config.xaxis,
        // xaxisにtypeが既にある場合はそれを保持、ない場合はoptionsから設定
        ...(config.xaxis?.type ? {} : 
            config.options?.xaxis_type ? { type: config.options.xaxis_type } : {}),
        // 10日以上のデータの場合、tickAmount をチャートの幅から決める(1日1目盛りと「幅 ÷ 60px」の小さい方。#1471)。
        // 幅が変わったら syncTickAmountToWidth が計算し直す。
        // 呼び出し側が xaxis.tickAmount を明示している場合は上書きしない(長期間でラベルが重なるのを呼び出し側で制御できるように)
        ...(autoTickPointCount ? {
          tickAmount: isHeatmap
            ? autoTickPointCount - 1
            : resolveDatetimeTickAmount({ pointCount: autoTickPointCount, chartWidth: this.measureChartWidth() })
        } : {}),
        labels: (() => {
          const isHorizontalBar = config.chart.type === 'bar' && config.plotOptions?.bar?.horizontal
          // numeric / category が明示された軸では数値をそのまま表示する
          // (型未指定の場合のみ従来どおり日付として解釈を試みる)
          const xaxisType = config.xaxis?.type || config.options?.xaxis_type
          const isNonDatetimeAxis = xaxisType && xaxisType !== 'datetime'
          const labelsConfig = {
            ...config.xaxis?.labels,
            formatter: (isHorizontalBar || isNonDatetimeAxis)
              ? function(value) {
                  return typeof value === 'number' ? new Intl.NumberFormat('ja-JP').format(value) : value
                }
              : function(value) {
                  const date = new Date(value)
                  return isNaN(date.getTime()) ? value : `${date.getMonth() + 1}/${date.getDate()}`
                },
            datetimeUTC: false  // 日本時間表示のためUTC無効化
          }

          if (config.chart.type !== 'heatmap' && !isHorizontalBar) {
            if (labelsConfig.rotate === undefined) {
              labelsConfig.rotate = -15
            }
            if (labelsConfig.rotateAlways === undefined) {
              labelsConfig.rotateAlways = true
            }
          }

          return labelsConfig
        })()
      },
      yaxis: Array.isArray(config.yaxis) ? config.yaxis.map(axis => ({
        ...axis,
        show: axis.show !== undefined ? axis.show : true,
        labels: {
          show: axis.labels?.show !== undefined ? axis.labels.show : true,
          formatter: resolveAxisLabelFormatter(axis)
        }
      })) : {
        ...config.yaxis,
        show: config.yaxis?.show !== undefined ? config.yaxis.show : true,
        labels: {
          show: config.yaxis?.labels?.show !== undefined ? config.yaxis.labels.show : true,
          formatter: resolveAxisLabelFormatter(config.yaxis)
        }
      },
      // Legend: center at bottom by default; chart_options.legend で指定したキーだけ上書きされる(下の applyPassthroughOptions)
      legend: buildDefaultLegend(this.buildLegendTooltipEvents(config)),
      grid: {
        padding: {
          right: 24
        }
      },
      colors: this.resolveColors(config),
      tooltip: buildDefaultTooltip({
        xaxisType: config.xaxis?.type || config.options?.xaxis_type,
        showTime: tooltipShowsTime,
        formatSeriesValue: this.formatSeriesValue
      }),
      // アノテーション（祝日・週末の帯色）の設定（必ず最後に設定）。
      // 優先順位: options.annotations > options.chart_options.annotations > {}
      annotations: (config.options?.annotations) || (config.options?.chart_options?.annotations) || {}
    }
    // 複数Y軸: ApexCharts は series[].yAxisIndex を読まないため、yAxisIndex から各軸の seriesName を組み立てる(#1359)。
    // 下のレスポンシブ設定は baseOptions.yaxis を複製するので、その前に行う
    baseOptions.yaxis = assignSeriesToYAxes(baseOptions.yaxis, baseOptions.series)
    // ヒートマップ専用設定
    if (config.chart.type === 'heatmap') {
      baseOptions.plotOptions = config.plotOptions
      baseOptions.dataLabels = {
        enabled: true,
        style: {
          colors: ['#fff']
        }
      }
      baseOptions.tooltip = {
        ...baseOptions.tooltip,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const data = config.series[seriesIndex].data[dataPointIndex]
          const xLabel = data.x || w.globals.labels[dataPointIndex]
          const yLabel = config.series[seriesIndex].name
          const value = data.value !== undefined ? data.value : data.y

          return `
            <div class="p-3 bg-white rounded shadow">
              <div class="font-medium">${yLabel}</div>
              <div class="text-sm text-gray-600">${xLabel}</div>
              <div class="text-lg font-bold">${this.formatNumber(value)}</div>
            </div>
          `
        }
      }
      baseOptions.chart.toolbar.show = false
    } else {
      baseOptions.dataLabels = this.buildDataLabelsOptions(config, { mobile })
      baseOptions.stroke = resolveInternalOption(config.options, 'stroke') || {
        // curve: 'smooth',
        width: 2
      }
      // ツールバーはプロット領域の右上に重なって描画されるため、
      // 表示時は上部に余白を確保してデータラベル（数値）との衝突を避ける
      const toolbarShown = baseOptions.chart.toolbar?.show !== false
      baseOptions.grid = {
        borderColor: '#E5E7EB', // gray-200
        strokeDashArray: 3,
        padding: {
          right: 24,
          ...(toolbarShown ? { top: 30 } : {})
        }
      }
      // 棒グラフ用のplotOptions
      if (config.chart.type === 'bar') {
        const isHorizontal = config.plotOptions?.bar?.horizontal
        baseOptions.plotOptions = {
          ...config.plotOptions,
          bar: {
            ...config.plotOptions?.bar,
            dataLabels: {
              position: isHorizontal ? 'center' : 'top'
            }
          }
        }
        if (isHorizontal) {
          baseOptions.dataLabels = {
            enabled: true,
            formatter: function(val) { return new Intl.NumberFormat('ja-JP').format(val) },
            style: { fontSize: '12px', fontWeight: 'bold', colors: ['#fff'] }
          }
          if (config.plotOptions?.bar?.distributed) {
            baseOptions.legend = { show: false }
          }
        }
        // 積み上げ棒はセグメント内ラベルが潰れて読めないため、
        // 合計値のみ頂点に表示し、内訳はツールチップで確認する
        if (config.chart.stacked && !isHorizontal) {
          baseOptions.dataLabels = { enabled: false }
          baseOptions.plotOptions.bar.dataLabels = {
            ...baseOptions.plotOptions.bar.dataLabels,
            total: {
              enabled: true,
              formatter: function(val) { return new Intl.NumberFormat('ja-JP').format(val) },
              style: { fontSize: '11px', fontWeight: 600, color: '#6b7280' }
            }
          }
        }
      }
    }

    // 日本語ロケール設定（datetimeチャート用）
    if (config.options?.xaxis_type === 'datetime' || config.xaxis?.type === 'datetime') {
      baseOptions.chart.defaultLocale = 'ja'
      baseOptions.chart.locales = [{
        name: 'ja',
        options: {
          months: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          shortMonths: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          days: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'],
          shortDays: ['日', '月', '火', '水', '木', '金', '土']
        }
      }]
    }

    // chart_options に書いた ApexCharts の設定(legend / markers / tooltip / grid / plotOptions など)を既定値に deep merge する(#1359)。
    // 既定値を作り終えた後に重ねるので、上で組み立てた grid / plotOptions なども指定したキーだけ上書きできる。
    // 既定の formatter やイベントハンドラは、関数以外の値では上書きされない(lib/apex_chart_options.js)
    applyPassthroughOptions(baseOptions, config.options)

    // スマホ幅(768px 未満)の設定: ツールバー非表示・小さいフォント・データラベルの間引き・x 軸の目盛り数・
    // mobile_primary_axis_only の右軸非表示(lib/apex_chart_options.js#buildMobileOverrideOptions、#1371 / #1486)。
    // ApexCharts の responsive は幅を広げても元に戻らないため使わず、描画時の幅で重ねるかを決める。
    // 幅が 768px をまたいだら handleViewportChange が作り直す。呼び出し側が responsive を渡していれば ApexCharts に任せる
    this.usesBuiltinViewportOptions = !usesCustomResponsive(baseOptions)
    const viewportOptions = applyViewportOptions(baseOptions, config, { mobile })

    this.attachDataPointNavigation(viewportOptions, config)
    this.attachTickAmountSync(viewportOptions, {
      // スマホ幅は目盛り数を上限 6 に固定する(buildMobileXaxisOptions)ので幅では計算し直さない
      pointCount: mobile || isHeatmap ? null : autoTickPointCount,
      tickAmount: viewportOptions.xaxis?.tickAmount
    })
    this.attachDataLabelLimitSync(viewportOptions)

    return viewportOptions
  }

  // チャート要素の幅(px)。非表示のタブの中などで測れなければ 0
  measureChartWidth() {
    if (!this.hasContainerTarget) return 0
    return this.containerTarget.clientWidth || 0
  }

  // 幅から決めた datetime 軸の目盛り数を、描画後(mounted)と再描画のたび(updated。ウィンドウや親要素の幅が変わると
  // ApexCharts が描き直して呼ぶ)に計算し直す(#1471)。値が変わったときだけ updateOptions する。
  // 幅はチャート要素の幅で測る(目盛り数で変わらない)ので、updateOptions の後の updated では同じ値になって止まる
  attachTickAmountSync(options, { pointCount, tickAmount }) {
    this.autoTickPointCount = pointCount
    this.appliedTickAmount = tickAmount
    if (!pointCount) return

    options.chart = options.chart || {}
    const existingEvents = options.chart.events || {}
    const syncHandler = (chartContext) => this.syncTickAmountToWidth(chartContext)
    options.chart.events = {
      ...existingEvents,
      mounted: this.combineGenericHandlers(existingEvents.mounted, syncHandler),
      updated: this.combineGenericHandlers(existingEvents.updated, syncHandler)
    }
  }

  syncTickAmountToWidth(chartContext) {
    if (!this.autoTickPointCount || !chartContext || chartContext !== this.chart) return

    const chartWidth = this.measureChartWidth()
    // 幅が測れない(非表示になった)ときはそのまま。表示されて描き直されたときに計算する
    if (chartWidth <= 0) return

    const tickAmount = resolveDatetimeTickAmount({ pointCount: this.autoTickPointCount, chartWidth })
    if (tickAmount === this.appliedTickAmount) return

    this.appliedTickAmount = tickAmount
    this.scheduleWidthSyncUpdate(chartContext, { xaxis: { tickAmount } })
  }

  // 幅に合わせたデータラベルの間引きを、目盛り数と同じく描画後(mounted)と再描画のたび(updated)に計算し直す(#1471)。
  // ズーム(Toolbar の zoomUpdateOptions)とリセット(_updateSeries)も描き直して updated を呼ぶので、ここで表示範囲の変化も拾う。
  // 間引きを使うチャート(デスクトップ幅・呼び出し側が間引きを指定していない)だけ登録する
  attachDataLabelLimitSync(options) {
    if (!this.dataLabelWidthLimitEnabled) return

    options.chart = options.chart || {}
    const existingEvents = options.chart.events || {}
    const syncHandler = (chartContext) => this.syncDataLabelsToView(chartContext)
    options.chart.events = {
      ...existingEvents,
      mounted: this.combineGenericHandlers(existingEvents.mounted, syncHandler),
      updated: this.combineGenericHandlers(existingEvents.updated, syncHandler)
    }
  }

  // いまの幅と表示範囲(ズーム中の xaxis.min / max)で表示する点を選び直し、変わったときだけ描き直す。
  // 点の少ないチャートは選ぶ点が変わらないので、幅が変わっても描き直さない。
  // 幅も表示範囲もデータラベルの描き直しでは変わらないので、描き直した後の updated では同じ結果になって止まる。
  // 更新するのは dataLabels だけ(xaxis を渡さないのでズームの範囲は保たれる)
  syncDataLabelsToView(chartContext) {
    if (!this.dataLabelWidthLimitEnabled || !chartContext || chartContext !== this.chart) return

    const chartWidth = this.measureChartWidth()
    // 幅が測れない(非表示になった)ときはそのまま。表示されて描き直されたときに計算する
    if (chartWidth <= 0) return

    const cache = this.dataLabelCache
    if (!cache) return
    const w = chartContext.w
    const xaxis = w?.config?.xaxis || {}
    // 描画後はプロット領域の実際の幅(gridWidth)で判定する。データラベルの描き直しでは変わらないので、ここでも止まる
    // ApexCharts 7 の正は w.layout.gridWidth(w.globals.gridWidth は互換用の別名)
    const gridWidth = Number(w?.layout?.gridWidth ?? w?.globals?.gridWidth)
    const displayMap = this.buildDataLabelDisplayMap(cache, {
      plotWidth: gridWidth > 0 ? gridWidth : estimatePlotWidth(chartWidth),
      range: { min: xaxis.min, max: xaxis.max, xaxisType: xaxis.type },
      w
    })
    const displayKey = this.dataLabelDisplayKey(displayMap)
    if (displayKey === cache.displayKey) return

    cache.displayMap = displayMap
    cache.displayKey = displayKey
    // formatter は dataLabelCache を読むので、設定は変えずに描き直すだけでよい
    this.scheduleWidthSyncUpdate(chartContext, { dataLabels: { enabled: chartContext.w?.config?.dataLabels?.enabled !== false } })
  }

  // 幅に合わせた更新(目盛り数・データラベル)を、ApexCharts の描画処理(mounted / updated の呼び出し元)が終わってから
  // まとめて 1 回の updateOptions にする(両方が同時に変わっても描き直しは 1 回)
  scheduleWidthSyncUpdate(chartContext, options) {
    this.pendingWidthSyncOptions = { ...(this.pendingWidthSyncOptions || {}), ...options }
    clearTimeout(this.widthSyncTimer)
    this.widthSyncTimer = setTimeout(() => {
      this.widthSyncTimer = null
      const pending = this.pendingWidthSyncOptions
      this.pendingWidthSyncOptions = null
      if (this.chart !== chartContext || !pending) return
      // redraw: false / animate: false / 同期チャートは更新しない / initialConfig は上書きしない
      // (overwriteInitialConfig の既定は true。ズーム中に上書きするとツールバーのリセットでズームが解除されなくなる)
      chartContext.updateOptions(pending, false, false, false, false)
    }, 0)
  }

  cancelWidthSyncUpdate() {
    clearTimeout(this.widthSyncTimer)
    this.widthSyncTimer = null
    this.pendingWidthSyncOptions = null
  }

  // 系列の色。ヘルパー経由(config.options.colors)と手組み(config.options.chart_options.colors)の両方を見て、chart_options を優先する
  resolveColors(config) {
    return resolveInternalOption(config.options, 'colors')
  }

  // チャート全体の Y 軸ラベル書式(options.yaxis_label_formatter)。使える名前は lib/apex_chart_label_formatters.js。
  // デフォルト: K/M/B単位の短縮表示
  resolveChartYAxisLabelFormatter(formatKey) {
    return resolveYAxisLabelFormatter(formatKey, formatCompactNumber)
  }


  // mobile: スマホ幅の設定を重ねるか。デスクトップ幅だけ、点の多い系列のラベル数を幅で抑える(#1471)
  buildDataLabelsOptions(config, { mobile = false } = {}) {
    const customOptions = config.options?.chart_options?.dataLabels ?? config.options?.dataLabels
    this.dataLabelSettings = this.extractDataLabelSamplingSettings(customOptions)
    // 横棒(数値は棒の中)と積み上げの縦棒(合計だけ頂点に出す)は、下で間引きを使わない dataLabels に置き換えるので対象外
    const isBarChart = config.chart.type === 'bar'
    const replacesDataLabels = isBarChart && (Boolean(config.plotOptions?.bar?.horizontal) || Boolean(config.chart.stacked))
    this.dataLabelWidthLimitEnabled = !mobile && !replacesDataLabels && !usesCustomDataLabelSampling(customOptions)
    this.setupDataLabelCaches(config)

    const controller = this
    const defaultFormatter = (value, context) => {
      if (controller.shouldSuppressDataLabel(value, context, config)) {
        return ''
      }

      // 系列が載る軸と同じ書式(null / undefined / 数値に直せない値はそのまま返す)
      const formatSeriesValue = controller.formatSeriesValue || buildSeriesValueFormatter()
      return formatSeriesValue(value, context)
    }

    // 棒グラフの場合は真上中央に配置、それ以外は左斜め上
    const baseOptions = {
      enabled: true,
      formatter: defaultFormatter,
      style: {
        fontSize: '10px',
        fontWeight: 'bold'
      },
      offsetX: isBarChart ? 0 : -8,
      offsetY: -5,
      textAnchor: isBarChart ? 'middle' : 'start',
      background: {
        enabled: false
      }
    }

    if (customOptions === false) {
      return { ...baseOptions, enabled: false }
    }

    if (typeof customOptions === 'object') {
      const sanitizedOptions = { ...customOptions }
      const customStyle = sanitizedOptions.style
      const customBackground = sanitizedOptions.background
      const customFormatter = sanitizedOptions.formatter
      delete sanitizedOptions.style
      delete sanitizedOptions.background
      delete sanitizedOptions.formatter
      delete sanitizedOptions.minSpacing
      delete sanitizedOptions.relativeThreshold
      delete sanitizedOptions.absoluteThreshold
      delete sanitizedOptions.minAbsolute

      const mergedStyle = { ...baseOptions.style, ...(customStyle || {}) }
      const mergedBackground = { ...baseOptions.background, ...(customBackground || {}) }
      const userFormatter = typeof customFormatter === 'function'
        ? (value, context) => {
            if (controller.shouldSuppressDataLabel(value, context, config)) {
              return ''
            }
            return customFormatter(value, context)
          }
        : null

      return {
        ...baseOptions,
        ...sanitizedOptions,
        style: mergedStyle,
        background: mergedBackground,
        formatter: userFormatter || baseOptions.formatter
      }
    }

    return baseOptions
  }

  shouldSuppressDataLabel(value, context, config) {
    const { seriesIndex, dataPointIndex } = context || {}

    const displayMap = this.dataLabelCache?.displayMap?.[seriesIndex]
    if (Array.isArray(displayMap)) {
      return !displayMap[dataPointIndex]
    }

    const numericValue = this.normalizeToNumber(
      value,
      this.extractDataPointValue(config, seriesIndex, dataPointIndex)
    )

    if (numericValue === null) {
      return false
    }

    return Math.abs(numericValue) <= 1e-9
  }

  extractDataPointValue(config, seriesIndex, dataPointIndex) {
    if (!config?.series || !Array.isArray(config.series)) {
      return null
    }

    const series = config.series[seriesIndex]
    if (!series || !Array.isArray(series.data)) {
      return null
    }

    const dataPoint = series.data[dataPointIndex]
    if (dataPoint === null || dataPoint === undefined) {
      return null
    }

    if (typeof dataPoint === 'number') {
      return dataPoint
    }

    if (Array.isArray(dataPoint) && dataPoint.length >= 2) {
      return this.normalizeToNumber(dataPoint[1])
    }

    if (typeof dataPoint === 'object') {
      if (dataPoint.y !== undefined) {
        return this.normalizeToNumber(dataPoint.y)
      }

      if (dataPoint.value !== undefined) {
        return this.normalizeToNumber(dataPoint.value)
      }
    }

    return null
  }

  normalizeToNumber(...candidates) {
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) {
        continue
      }

      if (typeof candidate === 'number') {
        if (Number.isFinite(candidate)) {
          return candidate
        }
        continue
      }

      if (typeof candidate === 'string') {
        const cleaned = candidate.trim().replace(/[,\s]/g, '').replace(/%$/, '')
        if (cleaned === '') {
          continue
        }

        const parsed = Number(cleaned)
        if (Number.isFinite(parsed)) {
          return parsed
        }
      }
    }

    return null
  }

  extractDataLabelSamplingSettings(customOptions) {
    const defaults = this.defaultDataLabelSamplingSettings()

    if (!customOptions || typeof customOptions !== 'object') {
      return defaults
    }

    const settings = {
      minSpacing: Number.isFinite(customOptions.minSpacing) ? Math.max(0, Math.floor(customOptions.minSpacing)) : defaults.minSpacing,
      relativeThreshold: Number.isFinite(customOptions.relativeThreshold) ? Math.max(0, customOptions.relativeThreshold) : defaults.relativeThreshold,
      minAbsolute: Number.isFinite(customOptions.minAbsolute) ? Math.max(0, customOptions.minAbsolute) : defaults.minAbsolute,
      absoluteThreshold: Number.isFinite(customOptions.absoluteThreshold) ? Math.max(0, customOptions.absoluteThreshold) : defaults.absoluteThreshold
    }

    return settings
  }

  defaultDataLabelSamplingSettings() {
    return {
      minSpacing: 5,
      relativeThreshold: 0.05,
      minAbsolute: 0,
      absoluteThreshold: null
    }
  }

  setupDataLabelCaches(config) {
    if (!config?.series || !Array.isArray(config.series)) {
      this.dataLabelCache = {
        valuesBySeries: [],
        rangesBySeries: [],
        thresholdsBySeries: [],
        xValuesBySeries: [],
        displayMap: []
      }
      return
    }

    const settings = this.dataLabelSettings || this.defaultDataLabelSamplingSettings()
    const valuesBySeries = config.series.map(series => this.createSeriesNumericValues(series))
    const rangesBySeries = valuesBySeries.map(values => this.computeSeriesRange(values))
    const thresholdsBySeries = rangesBySeries.map(range => this.calculateThreshold(range, settings))
    const xValuesBySeries = config.series.map(series => this.createSeriesXValues(series))
    const cache = { valuesBySeries, rangesBySeries, thresholdsBySeries, xValuesBySeries }
    cache.seriesNames = config.series.map(series => series?.name)
    // 描画前はチャート要素の幅からプロット領域の幅を見積もる(幅 0 なら間引かない。描画後に syncDataLabelsToView が w.layout.gridWidth で選び直す)
    cache.displayMap = this.buildDataLabelDisplayMap(cache, { plotWidth: estimatePlotWidth(this.measureChartWidth()) })
    cache.displayKey = this.dataLabelDisplayKey(cache.displayMap)

    this.dataLabelCache = cache
  }

  // 系列ごとに数値ラベルを出す点の旗を作る(#1471)。
  // plotWidth: プロット領域の幅(px)、range: ズーム中の xaxis.min / max と xaxis.type(無い・category 軸なら全体)、w: 描画後の ApexCharts の w
  // - 表示範囲内の点だけで従来の間引き(computeDisplayFlags)を計算し、デスクトップ幅では隣り合うラベルの間隔が
  //   1 ラベルあたりの幅より狭いときだけ等間隔にする(limitDataLabelFlags。最大・最小・最後の点は残す)。表示範囲の外の点は出さない
  // - 1 ラベルあたりの幅は、表示範囲の最大値・最小値をデータラベルと同じ書式に通した文字数で見積もる(estimateDataLabelWidth)
  // - 間引きを使わないチャート(スマホ幅など)で範囲が無ければ、従来どおり全体を computeDisplayFlags にかけた結果と同じ
  buildDataLabelDisplayMap(cache, { plotWidth = 0, range = {}, w = null } = {}) {
    const settings = this.dataLabelSettings || this.defaultDataLabelSamplingSettings()
    const formatSeriesValue = this.formatSeriesValue || buildSeriesValueFormatter()
    // 描画前は w が無いので、系列名(エンゲージメント率の % 表示の判定に使う)だけ渡す
    const formatterW = w || { globals: { seriesNames: cache.seriesNames || [] } }
    return cache.valuesBySeries.map((values, index) => {
      const { start, end } = resolveVisibleIndexRange(cache.xValuesBySeries?.[index] || [], range)
      const visibleValues = values.slice(start, end + 1)
      let visibleFlags = this.computeDisplayFlags(visibleValues, settings, cache.thresholdsBySeries[index])
      if (this.dataLabelWidthLimitEnabled) {
        const labelWidth = estimateDataLabelWidth(visibleValues, value => formatSeriesValue(value, { seriesIndex: index, w: formatterW }))
        visibleFlags = limitDataLabelFlags(visibleValues, visibleFlags, { plotWidth, labelWidth })
      }
      const flags = new Array(values.length).fill(false)
      visibleFlags.forEach((flag, offset) => { flags[start + offset] = flag })
      return flags
    })
  }

  // 選び直した結果が前と同じかを比べるための文字列
  dataLabelDisplayKey(displayMap) {
    return displayMap.map(flags => flags.map(flag => (flag ? '1' : '0')).join('')).join('|')
  }

  // 系列の各点の x。数値の x(datetime 軸のミリ秒・numeric 軸)だけ使い、それ以外(category 軸の文字列や数値だけの系列)は NaN。
  // NaN を含む系列はズーム中も全体で選ぶ(category 軸のズーム範囲は番号なので x と比べられない)
  createSeriesXValues(series) {
    if (!series || !Array.isArray(series.data)) return []

    return series.data.map(point => {
      const x = Array.isArray(point) ? point[0] : point?.x
      return typeof x === 'number' ? x : Number.NaN
    })
  }

  createSeriesNumericValues(series) {
    if (!series || !Array.isArray(series.data)) {
      return []
    }

    return series.data.map(point => {
      if (point === null || point === undefined) {
        return null
      }

      if (typeof point === 'number') {
        return Number.isFinite(point) ? point : null
      }

      if (Array.isArray(point) && point.length >= 2) {
        return this.normalizeToNumber(point[1])
      }

      if (typeof point === 'object') {
        return this.normalizeToNumber(point.y, point.value)
      }

      return this.normalizeToNumber(point)
    })
  }

  computeSeriesRange(values) {
    if (!Array.isArray(values) || values.length === 0) {
      return 0
    }

    let min = null
    let max = null

    values.forEach(value => {
      if (value === null) {
        return
      }

      if (min === null || value < min) {
        min = value
      }
      if (max === null || value > max) {
        max = value
      }
    })

    if (min === null || max === null) {
      return 0
    }

    return max - min
  }

  findPreviousValueIndex(values, index) {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (values[i] !== null && values[i] !== undefined) {
        return i
      }
    }

    return null
  }

  findNextValueIndex(values, index) {
    for (let i = index + 1; i < values.length; i += 1) {
      if (values[i] !== null && values[i] !== undefined) {
        return i
      }
    }

    return null
  }

  isLocalExtreme(values, index, threshold) {
    const current = values[index]
    if (current === null || current === undefined) {
      return false
    }

    const previousIndex = this.findPreviousValueIndex(values, index)
    const nextIndex = this.findNextValueIndex(values, index)

    if (previousIndex === null || nextIndex === null) {
      return false
    }

    const previous = values[previousIndex]
    const next = values[nextIndex]

    if (previous === null || next === null) {
      return false
    }

    const higherThanNeighbors = current > previous && current > next
    const lowerThanNeighbors = current < previous && current < next

    if (!higherThanNeighbors && !lowerThanNeighbors) {
      return false
    }

    const diffPrev = Math.abs(current - previous)
    const diffNext = Math.abs(current - next)

    const significantPrev = this.isSignificantDifference(diffPrev, threshold)
    const significantNext = this.isSignificantDifference(diffNext, threshold)

    return significantPrev || significantNext
  }

  isSignificantDifference(diff, threshold) {
    if (diff === null || diff === undefined) {
      return false
    }

    if (threshold === null || threshold === undefined) {
      return diff > 0
    }

    if (threshold <= 0) {
      return diff > 0
    }

    return diff >= threshold
  }

  isLastNonNull(values, index) {
    for (let i = index + 1; i < values.length; i += 1) {
      if (values[i] !== null && values[i] !== undefined) {
        return false
      }
    }

    return true
  }

  getDataLabelThreshold(seriesIndex) {
    const settings = this.dataLabelSettings || this.defaultDataLabelSamplingSettings()
    const thresholds = this.dataLabelCache?.thresholdsBySeries
    if (Array.isArray(thresholds)) {
      return thresholds[seriesIndex]
    }

    const range = this.dataLabelCache?.rangesBySeries?.[seriesIndex]
    return this.calculateThreshold(range, settings)
  }

  calculateThreshold(range, settings) {
    let threshold = 0
    if (Number.isFinite(range) && range > 0) {
      threshold = range * settings.relativeThreshold
    }

    if (settings.absoluteThreshold !== null) {
      threshold = Math.max(threshold, settings.absoluteThreshold)
    }

    if (settings.minAbsolute !== null && settings.minAbsolute !== undefined) {
      threshold = Math.max(threshold, settings.minAbsolute)
    }

    return threshold
  }

  computeDisplayFlags(values, settings, threshold) {
    if (!Array.isArray(values) || values.length === 0) {
      return []
    }

    const flags = new Array(values.length).fill(false)
    let lastDisplayedIndex = null
    let lastDisplayedValue = null
    let hasDisplayedNonZero = false

    for (let i = 0; i < values.length; i += 1) {
      const value = values[i]

      if (value === null || value === undefined) {
        continue
      }

      if (Math.abs(value) <= 1e-9) {
        continue
      }

      let display = false

      if (!hasDisplayedNonZero) {
        display = true
      } else {
        const diff = lastDisplayedValue === null ? null : Math.abs(value - lastDisplayedValue)
        const significantDiff = this.isSignificantDifference(diff, threshold)
        const spacingEnough = settings.minSpacing > 0
          ? (lastDisplayedIndex === null ? true : (i - lastDisplayedIndex) >= settings.minSpacing)
          : false
        const localExtreme = this.isLocalExtreme(values, i, threshold)
        const isLast = this.isLastNonNull(values, i)

        display = significantDiff || spacingEnough || localExtreme || isLast
      }

      if (display) {
        flags[i] = true
        lastDisplayedIndex = i
        lastDisplayedValue = value
        hasDisplayedNonZero = true
      }
    }

    return flags
  }

  buildToolbarOptions(config) {
    const baseToolbar = {
      show: config.chart.toolbar?.show !== false,
      tools: {
        download: true,
        zoom: config.chart.type !== 'heatmap',
        zoomin: config.chart.type !== 'heatmap',
        zoomout: config.chart.type !== 'heatmap',
        pan: false,
        reset: config.chart.type !== 'heatmap'
      }
    }

    const configuredExportOptions = config.export ||
                                    config.options?.export ||
                                    config.options?.chart_options?.export ||
                                    {}
    const { metadata: _metadata, ...exportOptions } = configuredExportOptions
    const csvOptions = exportOptions.csv || {}

    return {
      ...baseToolbar,
      ...(config.chart.toolbar || {}),
      export: {
        ...exportOptions,
        csv: {
          ...csvOptions,
          dateFormatter: csvOptions.dateFormatter || DEFAULT_CSV_DATE_FORMATTER,
          categoryFormatter: csvOptions.categoryFormatter || DEFAULT_CSV_CATEGORY_FORMATTER
        }
      }
    }
  }

  setupDiffTooltips(config) {
    if (!config.options.chart_options) {
      config.options.chart_options = {}
    }
    
    config.options.chart_options.tooltip = {
      shared: true,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        // datetime軸の場合はxAxisの値から日付を取得
        let formattedDate
        try {
          const timestamp = w.globals.seriesX[0][dataPointIndex] // 最初のシリーズのX値を取得
          if (timestamp) {
            const date = new Date(timestamp)
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')
            const hours = date.getHours().toString().padStart(2, '0')
            const minutes = date.getMinutes().toString().padStart(2, '0')
            formattedDate = `${year}/${month}/${day} ${hours}:${minutes}`
          } else {
            formattedDate = `データポイント ${dataPointIndex + 1}`
          }
        } catch (error) {
          formattedDate = `データポイント ${dataPointIndex + 1}`
        }
        
        let tooltipContent = `
          <div class="p-3 bg-white rounded shadow min-w-[200px]">
            <div class="font-medium text-gray-600 mb-2">${formattedDate}</div>
        `
        
        // 表示中の系列の情報のみ表示（現在のチャートの状態を確認）
        config.series.forEach((seriesData, index) => {
          // seriesの値がnullまたはundefinedの場合はスキップ（非表示）
          if (series[index] === null || series[index] === undefined) return
          
          const value = series[index][dataPointIndex]
          // 値がnullまたはundefinedの場合もスキップ
          if (value === null || value === undefined) return
          
          const color = this.resolveColors(config)?.[index]
          // 値と増減量は、系列が載る軸と同じ書式で出す(率の系列なら %。buildSeriesValueFormatter)
          const formatSeriesValue = this.formatSeriesValue || buildSeriesValueFormatter()
          const valueContext = { seriesIndex: index, w }
          
          tooltipContent += `
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center">
                <div class="w-3 h-3 rounded-full mr-2" style="background-color: ${color}"></div>
                <span class="text-sm">${seriesData.name}</span>
              </div>
              <span class="font-bold">${formatSeriesValue(value, valueContext)}</span>
            </div>
          `
          
          // 差分データがある場合。0 は矢印なしの「±0」、率は丸めて 0 にならない桁で出す(lib/apex_chart_diff.js、#1487)
          if (seriesData.diff && dataPointIndex > 0) {
            const summary = buildDiffSummary({
              diff: seriesData.diff.data?.[dataPointIndex],
              percentage: seriesData.diff.percentage?.[dataPointIndex],
              formatValue: (amount) => formatSeriesValue(amount, valueContext)
            })
            if (summary) {
              // 変化なしはグレーの文字(diff.colors.neutral は薄すぎて白背景で読めないため使わない)
              const diffColor = summary.direction === 'up'
                ? config.diff.colors?.positive
                : summary.direction === 'down' ? config.diff.colors?.negative : null
              const colorAttribute = diffColor ? ` style="color: ${diffColor}"` : ''
              const colorClass = diffColor ? '' : ' text-gray-500'

              tooltipContent += `
                <div class="text-xs ml-5 mb-1${colorClass}"${colorAttribute}>
                  ${summary.text}
                </div>
              `
            }
          }
        })
        
        tooltipContent += '</div>'
        return tooltipContent
      }
    }
  }

  setupKeywordBreakdownTooltip(config) {
    if (!config.options.chart_options) {
      config.options.chart_options = {}
    }

    const breakdown = config.options.keyword_breakdown
    if (!breakdown?.keywords || breakdown.keywords.length === 0) {
      return
    }

    config.options.chart_options.tooltip = {
      shared: true,
      intersect: false,
      custom: ({ series, dataPointIndex, w }) => {
        let formattedDate
        try {
          const timestamp = w.globals.seriesX[0][dataPointIndex]
          if (timestamp) {
            const date = new Date(timestamp)
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')
            formattedDate = `${year}/${month}/${day}`
          } else {
            formattedDate = `データポイント ${dataPointIndex + 1}`
          }
        } catch (error) {
          formattedDate = `データポイント ${dataPointIndex + 1}`
        }

        let tooltipContent = `
          <div class="p-3 bg-white rounded shadow min-w-[220px]">
            <div class="font-medium text-gray-600 mb-2">${formattedDate}</div>
        `

        config.series.forEach((seriesData, index) => {
          if (series[index] === null || series[index] === undefined) return

          const value = series[index][dataPointIndex]
          if (value === null || value === undefined) return

          const color = this.resolveColors(config)?.[index]
          tooltipContent += `
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center">
                <div class="w-3 h-3 rounded-full mr-2" style="background-color: ${color}"></div>
                <span class="text-sm">${seriesData.name}</span>
              </div>
              <span class="font-bold">${new Intl.NumberFormat('ja-JP').format(value)}</span>
            </div>
          `
        })

        const postsLabel = breakdown.labels?.posts || 'ポスト数'
        const likesLabel = breakdown.labels?.likes || 'いいね数'

        tooltipContent += `
            <div class="mt-2 text-xs text-gray-500">キーワード別内訳</div>
            <div class="mt-1 overflow-hidden rounded border border-gray-200">
              <table class="w-full text-xs">
                <thead class="bg-gray-50 text-gray-500">
                  <tr>
                    <th class="px-2 py-1 text-left font-medium">キーワード</th>
                    <th class="px-2 py-1 text-right font-medium">${postsLabel}</th>
                    <th class="px-2 py-1 text-right font-medium">${likesLabel}</th>
                  </tr>
                </thead>
                <tbody class="text-gray-700">
        `

        breakdown.keywords.forEach((keyword) => {
          const postsValue = keyword.posts?.[dataPointIndex] ?? 0
          const likesValue = keyword.likes?.[dataPointIndex] ?? 0

          tooltipContent += `
                  <tr class="border-t border-gray-200">
                    <td class="px-2 py-1 text-left">${keyword.name}</td>
                    <td class="px-2 py-1 text-right">${new Intl.NumberFormat('ja-JP').format(postsValue)}</td>
                    <td class="px-2 py-1 text-right">${new Intl.NumberFormat('ja-JP').format(likesValue)}</td>
                  </tr>
          `
        })

        tooltipContent += `
                </tbody>
              </table>
            </div>
        `

        tooltipContent += '</div>'
        return tooltipContent
      }
    }
  }

  ensureVisibleSeries(config) {
    // seriesが存在しない場合は何もしない
    if (!config.series || config.series.length === 0) {
      return
    }
    
    // すべてのseriesがhiddenかチェック
    const allHidden = config.series.every(series => series.hidden === true)
    
    // すべてがhiddenの場合、最初のseriesを表示状態にする
    if (allHidden) {
      console.warn("All series are hidden. Making the first series visible to prevent chart errors.")
      config.series[0].hidden = false
    }
  }

  formatNumber(num) {
    return new Intl.NumberFormat('ja-JP').format(num)
  }

  buildLegendTooltipEvents(config) {
    // seriesにdescriptionがあるかチェック
    const hasDescriptions = config.series?.some(s => s.description)
    if (!hasDescriptions) {
      return {}
    }

    const controller = this
    return {
      onItemClick: {
        toggleDataSeries: true
      },
      customLegendItems: [],
      markers: {
        onClick: undefined
      },
      onItemHover: {
        highlightDataSeries: true
      }
    }
  }

  setupLegendTooltips(config) {
    // seriesにdescriptionがあるかチェック
    const hasDescriptions = config.series?.some(s => s.description)
    if (!hasDescriptions) {
      return
    }

    // ツールチップ用のDOM要素を作成
    if (!this.legendTooltip) {
      this.legendTooltip = document.createElement('div')
      this.legendTooltip.className = 'apex-legend-tooltip'
      this.legendTooltip.style.cssText = `
        position: fixed;
        background: #1f2937;
        color: white;
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 12px;
        max-width: 300px;
        z-index: 9999;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      `
      document.body.appendChild(this.legendTooltip)
    }

    // 凡例アイテムにイベントリスナーを追加
    setTimeout(() => {
      this.attachLegendListeners(config)
    }, 100)
  }

  attachLegendListeners(config) {
    if (!this.chart?.el) return

    // 既存のリスナーを削除
    if (this.legendMouseEnterHandler) {
      this.chart.el.removeEventListener('mouseover', this.legendMouseEnterHandler)
      this.chart.el.removeEventListener('mouseout', this.legendMouseOutHandler)
      this.chart.el.removeEventListener('mousemove', this.legendMouseMoveHandler)
      this.chart.el.removeEventListener('click', this.legendClickHandler)
    }

    // シリーズ名とdescriptionのマッピングを作成
    this.seriesDescriptions = {}
    config.series.forEach((series) => {
      if (series?.description) {
        this.seriesDescriptions[series.name] = series.description
      }
    })

    // イベントデリゲーションでイベントを処理
    this.legendMouseEnterHandler = (e) => {
      const legendItem = e.target.closest('.apexcharts-legend-series')
      if (!legendItem) return

      const seriesName = legendItem.querySelector('.apexcharts-legend-text')?.textContent
      const description = this.seriesDescriptions[seriesName]
      if (description) {
        legendItem.style.cursor = 'help'
        this.showLegendTooltip(description, e)
      }
    }

    this.legendMouseOutHandler = (e) => {
      const legendItem = e.target.closest('.apexcharts-legend-series')
      if (legendItem) {
        this.hideLegendTooltip()
      }
    }

    this.legendMouseMoveHandler = (e) => {
      const legendItem = e.target.closest('.apexcharts-legend-series')
      if (legendItem && this.legendTooltip?.style.opacity === '1') {
        this.updateLegendTooltipPosition(e)
      }
    }

    this.legendClickHandler = () => {
      this.hideLegendTooltip()
    }

    this.chart.el.addEventListener('mouseover', this.legendMouseEnterHandler)
    this.chart.el.addEventListener('mouseout', this.legendMouseOutHandler)
    this.chart.el.addEventListener('mousemove', this.legendMouseMoveHandler)
    this.chart.el.addEventListener('click', this.legendClickHandler)
  }

  showLegendTooltip(description, event) {
    if (!this.legendTooltip) return

    this.legendTooltip.textContent = description
    this.legendTooltip.style.opacity = '1'
    this.updateLegendTooltipPosition(event)
  }

  updateLegendTooltipPosition(event) {
    if (!this.legendTooltip) return

    const x = event.clientX + 10
    const y = event.clientY + 10

    // 画面外にはみ出さないように調整
    const rect = this.legendTooltip.getBoundingClientRect()
    const maxX = window.innerWidth - rect.width - 10
    const maxY = window.innerHeight - rect.height - 10

    this.legendTooltip.style.left = `${Math.min(x, maxX)}px`
    this.legendTooltip.style.top = `${Math.min(y, maxY)}px`
  }

  hideLegendTooltip() {
    if (!this.legendTooltip) return
    this.legendTooltip.style.opacity = '0'
  }

  cleanupLegendTooltip() {
    if (this.legendTooltip) {
      this.legendTooltip.remove()
      this.legendTooltip = null
    }
  }

  setupStoryDatetimeTooltip(config) {
    if (!config.options.chart_options) {
      config.options.chart_options = {}
    }
    
    config.options.chart_options.tooltip = {
      shared: false,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        // timestamp配列から日時を取得
        let formattedDate = ''
        try {
          const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
          if (timestamp) {
            const date = new Date(timestamp)
            const hour = date.getHours().toString().padStart(2, '0')
            const minute = date.getMinutes().toString().padStart(2, '0')
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')
            formattedDate = `${year}/${month}/${day} ${hour}:${minute}`
          }
        } catch (error) {
          formattedDate = 'N/A'
        }
        
        const value = series[seriesIndex][dataPointIndex]
        const seriesName = config.series[seriesIndex].name
        const color = this.resolveColors(config)?.[seriesIndex] || '#3b82f6'
        
        return `
          <div class="p-3 bg-white rounded shadow min-w-[200px]">
            <div class="font-medium text-gray-600 mb-2">${formattedDate}</div>
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-3 h-3 rounded-full mr-2" style="background-color: ${color}"></div>
                <span class="text-sm">${seriesName}</span>
              </div>
              <span class="font-bold">${new Intl.NumberFormat('ja-JP').format(value)}</span>
            </div>
          </div>
        `
      }
    }
  }

  attachDataPointNavigation(options, config) {
    if (!config?.series || !Array.isArray(config.series) || config.series.length === 0) {
      return
    }

    if (config.chart?.type === 'heatmap') {
      return
    }

    options.chart = options.chart || {}
    const existingEvents = options.chart.events || {}
    const navigationHandler = (event, chartContext, dataPointContext) => {
      this.handleDataPointNavigation(dataPointContext, config, { event, chartContext })
    }

    const pointerHandler = (event, chartContext, dataPointContext) => {
      if (!dataPointContext) {
        this.setPointerCursor(false, { event, chartContext })
        return
      }

      const { seriesIndex, dataPointIndex, w } = dataPointContext
      const series = w?.config?.series?.[seriesIndex]
      const dataPoint = series?.data?.[dataPointIndex]
      const hasLink = !!this.extractDetailUrl(dataPoint, series, dataPointIndex)
      this.setPointerCursor(hasLink, { event, chartContext })
    }

    const pointerLeaveHandler = (event, chartContext) => {
      this.setPointerCursor(false, { event, chartContext })
    }

    const lifecycleHandler = () => {
      moveAnnotationsToBack(this.chart?.el)
      this.hideOverlappingAnnotationLabels(config)
    }

    options.chart.events = {
      ...existingEvents,
      dataPointSelection: this.combineEventHandlers(existingEvents.dataPointSelection, navigationHandler),
      markerClick: this.combineEventHandlers(existingEvents.markerClick, navigationHandler),
      dataPointMouseEnter: this.combineEventHandlers(existingEvents.dataPointMouseEnter, pointerHandler),
      dataPointMouseLeave: this.combineEventHandlers(existingEvents.dataPointMouseLeave, pointerLeaveHandler),
      mounted: this.combineGenericHandlers(existingEvents.mounted, lifecycleHandler),
      updated: this.combineGenericHandlers(existingEvents.updated, lifecycleHandler)
    }
  }

  // options.hide_overlapping_xaxis_annotation_labels が true のチャートだけ、重なった x 軸注釈のラベルを隠す。
  // ラベルは再描画(リサイズ・凡例クリック・ズーム)で描き直されるので、描画後と mounted / updated のたびに呼ぶ。
  // getBBox が描画前に 0 を返す場合に備え、moveAnnotationsToBack と同じく少し遅らせてもう1回呼ぶ
  hideOverlappingAnnotationLabels(config) {
    if (resolveInternalOption(config?.options, 'hide_overlapping_xaxis_annotation_labels') !== true) return

    const run = () => {
      const chart = this.chart
      if (!chart?.el) return
      hideOverlappingXaxisAnnotationLabels(chart.el, chart)
    }
    run()
    setTimeout(run, 50)
  }

  combineEventHandlers(originalHandler, navigationHandler) {
    if (typeof originalHandler === 'function') {
      return (event, chartContext, dataPointContext) => {
        originalHandler.call(chartContext, event, chartContext, dataPointContext)
        navigationHandler(event, chartContext, dataPointContext)
      }
    }

    return navigationHandler
  }

  combineGenericHandlers(originalHandler, newHandler) {
    if (typeof newHandler !== 'function') {
      return originalHandler
    }

    if (typeof originalHandler === 'function') {
      return (...args) => {
        originalHandler(...args)
        newHandler(...args)
      }
    }

    return newHandler
  }

  handleDataPointNavigation(dataPointContext, config, { event, chartContext } = {}) {
    if (!dataPointContext) {
      return false
    }

    const { seriesIndex, dataPointIndex, w } = dataPointContext
    if (seriesIndex === undefined || dataPointIndex === undefined) {
      return false
    }

    const series = w?.config?.series?.[seriesIndex]
    if (!series || !Array.isArray(series.data)) {
      return false
    }

    const dataPoint = series.data[dataPointIndex]
    const detailUrl = this.extractDetailUrl(dataPoint, series, dataPointIndex)
    if (!detailUrl) {
      return false
    }

    const navigate = () => this.navigateToDetail(detailUrl, config, { seriesIndex, dataPointIndex, dataPoint, event, chartContext })
    // ドラッグ(ズームの範囲選択)なら遷移しない。柱の dataPointSelection(mousedown)は離すまで保留する
    if (!this.clickGuard) return navigate()

    return this.clickGuard.requestNavigation(event, navigate) === 'navigated'
  }

  navigateToDetail(detailUrl, config, { seriesIndex, dataPointIndex, dataPoint, event, chartContext } = {}) {
    const navigationOptions = {
      openInNewTab: true,
      ...(config.options?.navigation || {})
    }

    if (navigationOptions.behavior === 'emit') {
      const eventName = navigationOptions.eventName || 'chart:point-click'
      const payload = {
        detailUrl,
        seriesIndex,
        dataPointIndex,
        dataPoint,
        originalEvent: event,
        chartContext
      }
      this.element.dispatchEvent(new CustomEvent(eventName, { detail: payload, bubbles: true }))
      return true
    }

    if (navigationOptions.openInNewTab) {
      window.open(detailUrl, '_blank', 'noopener')
      return true
    }

    if (window.Turbo?.visit && navigationOptions.behavior !== 'location') {
      window.Turbo.visit(detailUrl)
    } else {
      window.location.assign(detailUrl)
    }

    return true
  }

  extractDetailUrl(dataPoint, series, dataPointIndex) {
    if (!dataPoint) {
      return null
    }

    if (typeof dataPoint === 'object' && !Array.isArray(dataPoint)) {
      const directUrl = dataPoint.detail_url || dataPoint.detailUrl || dataPoint.url
      if (directUrl) {
        return directUrl
      }

      if (dataPoint.meta && typeof dataPoint.meta === 'object') {
        return dataPoint.meta.detail_url || dataPoint.meta.detailUrl || null
      }
    }

    if (Array.isArray(series?.detail_urls)) {
      return series.detail_urls[dataPointIndex]
    }

    if (Array.isArray(series?.detailUrls)) {
      return series.detailUrls[dataPointIndex]
    }

    return null
  }

  updateClickableClass(config) {
    const hasLinks = this.hasClickablePoints(config)

    if (hasLinks) {
      this.element.classList.add('apex-chart--clickable')
      this.element.style.cursor = 'pointer'
      if (this.chart?.el) {
        this.chart.el.classList.add('apex-chart--clickable')
        this.chart.el.style.cursor = 'pointer'
      }
    } else {
      this.element.classList.remove('apex-chart--clickable')
      this.element.style.cursor = ''
      if (this.chart?.el) {
        this.chart.el.classList.remove('apex-chart--clickable')
        this.chart.el.style.cursor = ''
      }
    }
  }

  hasClickablePoints(config) {
    const seriesList = config?.series
    if (!Array.isArray(seriesList)) {
      return false
    }

    return seriesList.some(series => {
      if (!series) return false

      if (Array.isArray(series.detail_urls) && series.detail_urls.some(url => !!url)) {
        return true
      }

      if (Array.isArray(series.detailUrls) && series.detailUrls.some(url => !!url)) {
        return true
      }

      if (!Array.isArray(series.data)) {
        return false
      }

      return series.data.some(point => this.pointHasDetailUrl(point))
    })
  }

  pointHasDetailUrl(point) {
    if (!point) {
      return false
    }

    if (typeof point === 'object') {
      if (point.detail_url || point.detailUrl || point.url) {
        return true
      }

      if (point.meta && typeof point.meta === 'object' && (point.meta.detail_url || point.meta.detailUrl)) {
        return true
      }
    }

    return false
  }

  setPointerCursor(isPointer, context = {}) {
    const cursor = isPointer ? 'pointer' : ''

    this.updateCursor(context.event?.target, cursor)

    if (this.chart?.el) {
      this.chart.el.style.cursor = cursor
    }

    if (this.hasContainerTarget) {
      this.containerTarget.style.cursor = cursor
    }

    this.element.style.cursor = cursor
  }

  updateCursor(target, cursor) {
    if (!(target instanceof Element)) {
      return
    }

    target.style.cursor = cursor

    let current = target.parentElement
    while (current instanceof Element && current !== this.element) {
      current.style.cursor = cursor
      current = current.parentElement
    }
  }

}
